import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { Model } from 'app/model/backendmodel';
import { IModel } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { MariaDBLinkedService } from 'app/model/linkedservice/maria-db.linkedservice';
import { akvToResource, akvToModel, parameterizableSecretBaseToModel, parameterizablePasswordOrAKVtoSecretBase } from 'app/model/adapters/linkedservices/azure-key-vault.util';
import { isString } from '../../../shared/utils/string.util';
import { SecureStringReplacer } from 'app/shared/utils/securestring-replacer';
import { Constants } from '../../../shared/shared-constants';
import { DbConnectionStringBuilderService } from 'app/services/db-connection-string-builder.service';
import { AzureKeyVault } from 'app/model/linkedservice/key-vault';
import { AdapterContext } from 'shell-models-lib';
import { driverVersions } from 'app/registries/consts/tabular-consts';
import { connectionStringPartToModel, connectionStringPartToResource } from 'app/shared/utils/linkedservice-parameter.util';
import { ValueAdapter } from '../value.adapter';
import { SSLMode, UseSystemTrustStoreMode } from 'app/registries/consts/mysql-consts';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(MariaDBLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                server: '*',
                database: '*',
                username: '*',
                port: '*',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                connectionString: (_context: AdapterContext, _value: any, ls: MariaDBLinkedService) => {
                    // if password is in AKV, then connectionString won't contain secret
                    if (ls && !ls.fullyByJsonEditor && !ls.enableAKV && ls.password instanceof AzureKeyVault) {
                        return '*';
                    }
                    return 'secret';
                },
                pwd: 'secret',
                password: 'secret',
                encryptedCredential: '*',
                sslMode: '*',
                useSystemTrustStore: '*',
                driverVersion: '*'
            },
            parameters: '*'
        },
    }
})
export class MariaDBLinkedServiceAdapter extends LinkedServiceAdapter<MariaDBLinkedService> {
    protected supportFirstClassParameterExperience = true;

    protected modelConstructor(): new () => MariaDBLinkedService {
        return MariaDBLinkedService;
    }

    protected model(): IModel {
        return MariaDBLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(linkedService: MariaDBLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.MariaDBLinkedService;
        properties.type = Metamodel.modelFor(MariaDBLinkedService).name;

        if (linkedService.driverVersion === driverVersions.v2) {
            properties.typeProperties = {
                server: this.valueToResource(linkedService, MariaDBLinkedService.SERVER_NAME),
                port: this.valueToResource(linkedService, MariaDBLinkedService.PORT),
                database: this.valueToResource(linkedService, MariaDBLinkedService.DATABASE_NAME),
                username: this.valueToResource(linkedService, MariaDBLinkedService.USER_NAME),
                password: parameterizablePasswordOrAKVtoSecretBase(linkedService, MariaDBLinkedService.PASSWORD, this.context),
                driverVersion: driverVersions.v2,
                sslMode: linkedService.sslMode != null ? linkedService.sslMode : undefined,
                useSystemTrustStore: linkedService.sslMode !== SSLMode.Disabled
                    ? linkedService.useSystemTrustStore
                    : undefined,
            };
        } else {
            properties.typeProperties = {
                connectionString: this._getConnectionStringResource(linkedService),
                pwd: !linkedService.enableAKV && linkedService.password instanceof AzureKeyVault ? akvToResource(linkedService.password, this.context) : undefined
            };
        }


        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, linkedService: MariaDBLinkedService): Promise<MariaDBLinkedService> {
        const properties = linkedServiceResource.properties as Model.MariaDBLinkedService;
        const typeProperties: Model.MariaDBLinkedServiceTypeProperties = properties.typeProperties;


        // Cleanup, in case none of the if-else statement got executed
        MariaDBLinkedService.SERVER_NAME.setValue(linkedService, undefined);
        MariaDBLinkedService.SERVER_NAME.setTemplateValue(linkedService, undefined);
        MariaDBLinkedService.DATABASE_NAME.setValue(linkedService, undefined);
        MariaDBLinkedService.DATABASE_NAME.setTemplateValue(linkedService, undefined);
        MariaDBLinkedService.PORT.setValue(linkedService, undefined);
        MariaDBLinkedService.PORT.setTemplateValue(linkedService, undefined);
        MariaDBLinkedService.USER_NAME.setValue(linkedService, undefined);
        MariaDBLinkedService.USER_NAME.setTemplateValue(linkedService, undefined);
        MariaDBLinkedService.PASSWORD.setValue(linkedService, undefined);
        MariaDBLinkedService.PASSWORD.setTemplateValue(linkedService, undefined);

        if (typeProperties.driverVersion === driverVersions.v2) {
            linkedService.isLegacyConnector = false;
            linkedService.driverVersion = driverVersions.v2;
            ValueAdapter.toModel(linkedService, MariaDBLinkedService.SERVER_NAME, typeProperties.server);
            ValueAdapter.toModel(linkedService, MariaDBLinkedService.DATABASE_NAME, typeProperties.database);
            ValueAdapter.toModel(linkedService, MariaDBLinkedService.PORT, typeProperties.port);
            ValueAdapter.toModel(linkedService, MariaDBLinkedService.USER_NAME, typeProperties.username);
            parameterizableSecretBaseToModel(linkedService, MariaDBLinkedService.PASSWORD, properties.typeProperties.password, this.context);
            linkedService.sslMode = typeProperties.sslMode != null ? typeProperties.sslMode : SSLMode.Preferred;
            linkedService.useSystemTrustStore = typeProperties.useSystemTrustStore ?? UseSystemTrustStoreMode.NotUse;
        } else {
            linkedService.driverVersion = driverVersions.v1;
            linkedService.isLegacyConnector = true;
            if (isString(typeProperties.connectionString)) {
                const builder = new DbConnectionStringBuilderService();
                builder.parse(typeProperties.connectionString as string);

                connectionStringPartToModel(linkedService, MariaDBLinkedService.SERVER_NAME, builder.getValue('Server', false) || '');
                connectionStringPartToModel(linkedService, MariaDBLinkedService.DATABASE_NAME, builder.getValue('Database', false) || '');
                connectionStringPartToModel(linkedService, MariaDBLinkedService.PORT, builder.getValue('Port', false) || undefined);
                connectionStringPartToModel(linkedService, MariaDBLinkedService.USER_NAME, builder.getValue('UID', false) || '');

                linkedService.password = Constants.maskedCredential;
                linkedService.additionalFields = builder.getRemainedKeyValuePairs(['Server', 'Port', 'Database', 'UID', 'PWD']);
                if (properties.typeProperties.pwd) {
                    parameterizableSecretBaseToModel(linkedService, MariaDBLinkedService.PASSWORD, properties.typeProperties.pwd, this.context, false);
                }
            } else if (SecureStringReplacer.isSecureStringObject(properties.typeProperties.connectionString)) {
                linkedService.server = Constants.maskedCredential;
                linkedService.databaseName = Constants.maskedCredential;
                linkedService.userName = Constants.maskedCredential;
                linkedService.password = Constants.maskedCredential;
                linkedService.port = undefined;
                if (properties.typeProperties.pwd) {
                    parameterizableSecretBaseToModel(linkedService, MariaDBLinkedService.PASSWORD, properties.typeProperties.pwd, this.context, false);
                }
            } else if (properties.typeProperties.connectionString
                && (properties.typeProperties.connectionString as Model.SecretBase).type === 'AzureKeyVaultSecret') {
                linkedService.referedAKV = akvToModel(properties.typeProperties.connectionString as Model.AzureKeyVaultSecretReference, this.context);
                linkedService.enableAKV = true;
            }
        }
        return linkedService;
    }


    private _getConnectionStringResource(linkedService: MariaDBLinkedService): string | Model.SecretBase {
        let connectionString: string | Model.SecretBase;
        if (!linkedService.enableAKV) {
            const builder = new DbConnectionStringBuilderService(true);
            builder.add('Server', connectionStringPartToResource(linkedService, MariaDBLinkedService.SERVER_NAME));
            builder.add('Port', connectionStringPartToResource(linkedService, MariaDBLinkedService.PORT));
            builder.add('Database', connectionStringPartToResource(linkedService, MariaDBLinkedService.DATABASE_NAME));
            builder.add('UID', connectionStringPartToResource(linkedService, MariaDBLinkedService.USER_NAME));
            if (linkedService.password && !(linkedService.password instanceof AzureKeyVault)) {
                builder.add('PWD', connectionStringPartToResource(linkedService, MariaDBLinkedService.PASSWORD));
            }
            if (linkedService.additionalFields) {
                linkedService.additionalFields.forEach((pair) => {
                    builder.add(pair.key, pair.value || '');
                });
            }
            connectionString = linkedService.server === Constants.maskedCredential &&
                linkedService.port === undefined &&
                linkedService.databaseName === Constants.maskedCredential &&
                linkedService.userName === Constants.maskedCredential &&
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                linkedService.password === Constants.maskedCredential ? {
                    type: 'SecureString',
                    value: Constants.maskedCredential
                } as Model.SecureString : this._connectionStringToResource(builder.connectionString);
        } else {
            connectionString = akvToResource(linkedService.referedAKV, this.context);
        }
        return connectionString;
    }
}
